<template>
  <div class="ml-16 max-w-7xl lg:flex lg:gap-x-10 lg:px-8">
    <h1 class="sr-only">Lead Details</h1>

    <aside class="flex overflow-x-auto border-b border-gray-900/5 py-2 lg:block lg:w-64 lg:flex-none lg:border-0">
      <nav class="flex-none px-4 sm:px-6 lg:px-0">
        <ul role="list" class="flex gap-x-3 gap-y-0.5 whitespace-nowrap lg:flex-col">
          <li>
            <span class="group flex gap-x-2.5 rounded-md bg-gray-50 py-1.5 pl-2 pr-2.5 text-sm font-semibold leading-6 text-[#2D8CFF]">
              <UserCircleIcon class="h-5 w-5 shrink-0 text-[#2D8CFF]" />
              Profile
            </span>
          </li>
        </ul>
      </nav>
    </aside>

    <main class="px-4 py-2 sm:px-6 lg:flex-auto lg:px-0">
      <div class="mx-auto max-w-2xl space-y-4 sm:space-y-5 lg:mx-0 lg:max-w-none">
        <div class="rounded-xl border border-zinc-200 bg-white p-4">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-sm font-semibold text-zinc-600">
              {{ leadInitials }}
            </div>
            <div class="min-w-0">
              <p class="truncate text-sm font-semibold text-zinc-900">
                {{ leadName }}
              </p>
              <p class="truncate text-xs text-zinc-500">
                {{ leadEmail }}
              </p>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-zinc-200 bg-white p-4">
          <h2 class="text-sm font-semibold text-zinc-900">Lead Profile</h2>
          <div class="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Status</p>
              <p class="mt-1 text-sm text-zinc-800">{{ leadStatusLabel }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Source</p>
              <p class="mt-1 text-sm text-zinc-800">{{ leadSource }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Selected Plan</p>
              <p class="mt-1 text-sm text-zinc-800">{{ leadPlanSlug }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Captured On</p>
              <p class="mt-1 text-sm text-zinc-800">{{ leadCreatedAt }}</p>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-600">
          {{ errorMessage }}
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { UserCircleIcon } from '@heroicons/vue/24/outline';

const route = useRoute();
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const loading = ref(false);
const errorMessage = ref('');
const lead = ref(null);

const leadId = computed(() => Number(route.params?.leadId || 0));
const leadName = computed(() => {
  const first = String(lead.value?.firstName || '').trim();
  const last = String(lead.value?.lastName || '').trim();
  return `${first} ${last}`.trim() || 'Lead';
});
const leadEmail = computed(() => String(lead.value?.email || '').trim());
const leadStatusLabel = computed(() => {
  const token = String(lead.value?.status || '').trim().toLowerCase();
  if (token === 'onboarding') return 'Onboarding';
  if (token === 'pending_payment') return 'Pending Payment';
  return 'New';
});
const leadSource = computed(() => {
  const token = String(lead.value?.source || '').trim();
  return token ? token.replace(/_/g, ' ') : 'Unknown';
});
const leadPlanSlug = computed(() => String(lead.value?.planTierSlug || '').trim() || 'Not selected');
const leadCreatedAt = computed(() => {
  const value = lead.value?.createdAt;
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
});
const leadInitials = computed(() => {
  const first = String(lead.value?.firstName || '').trim().charAt(0).toUpperCase();
  const last = String(lead.value?.lastName || '').trim().charAt(0).toUpperCase();
  return `${first}${last}`.trim() || 'L';
});

const loadLead = async () => {
  if (!leadId.value) {
    lead.value = null;
    return;
  }
  loading.value = true;
  errorMessage.value = '';
  try {
    const response = await fetch(`${apiProxyUrl}/api/leads/${leadId.value}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.success === false || !data?.lead) {
      throw new Error(String(data?.message || data?.error || 'Failed to load lead details'));
    }
    lead.value = data.lead;
  } catch (error) {
    lead.value = null;
    errorMessage.value = String(error?.message || 'Failed to load lead details');
  } finally {
    loading.value = false;
  }
};

watch(() => leadId.value, () => {
  void loadLead();
}, { immediate: true });

onMounted(() => {
  void loadLead();
});
</script>
